import React from 'react';
import { useTranslation } from 'localization';
import { Checkbox } from 'primereact/checkbox'; // No CheckboxAtom exists yet
import CustomDialog from '../../atoms/CustomDialog/CustomDialogAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';
import type { DataListSettings } from './useDataListSettings';
import './DataListSettingsPanel.scss';

export interface DataListSettingsPanelProps {
  /** Whether the panel is visible */
  visible: boolean;
  /** Callback to close the panel */
  onHide: () => void;
  /** Current settings */
  settings: DataListSettings;
  /** Column definitions (for visibility toggles) */
  columns: DataGridColumn[];
  /** Toggle column visibility */
  onToggleColumn: (field: string) => void;
  /** Update a setting */
  onUpdateSetting: <K extends keyof DataListSettings>(key: K, value: DataListSettings[K]) => void;
  /** Reset to defaults */
  onReset: () => void;
  /** Available rows per page options */
  rowsPerPageOptions?: number[];
  /** Custom settings content (rendered below built-in settings) */
  customSettings?: React.ReactNode;
}

const DataListSettingsPanel: React.FC<DataListSettingsPanelProps> = ({
  visible,
  onHide,
  settings,
  columns,
  onToggleColumn,
  onUpdateSetting,
  onReset,
  rowsPerPageOptions = [5, 10, 25, 50],
  customSettings,
}) => {
  const { t } = useTranslation();

  const sortOrderOptions = [
    { label: t('dataList.settings.ascending', 'Ascending'), value: 'asc' },
    { label: t('dataList.settings.descending', 'Descending'), value: 'desc' },
  ];

  // Filter out actions column and columns without headers
  const toggleableColumns = columns.filter(
    (col) => col.field !== 'actions' && col.header
  );

  // Build sort options from columns that are sortable
  const sortOptions = [
    { label: t('dataList.settings.sortNone', 'None'), value: '' },
    ...columns
      .filter((col) => col.sortable && col.header)
      .map((col) => ({
        label: String(col.header),
        value: col.field,
      })),
  ];

  const rowsOptions = rowsPerPageOptions.map((n) => ({
    label: String(n),
    value: n,
  }));

  const footer = (
    <div className="data-list-settings-panel__footer">
      <ButtonAtom
        label={t('dataList.settings.reset', 'Reset to Defaults')}
        icon="pi pi-refresh"
        variant="text"
        onClick={onReset}
      />
      <ButtonAtom
        label={t('dataList.settings.done', 'Done')}
        icon="pi pi-check"
        variant="primary"
        onClick={onHide}
      />
    </div>
  );

  return (
    <CustomDialog
      header={t('dataList.settings.title', 'Settings')}
      visible={visible}
      onHide={onHide}
      footer={footer}
      className="data-list-settings-panel"
      style={{ width: '400px', maxWidth: '90vw', minHeight: 'auto' }}
      showCloseIcon={true}
    >
      <div className="data-list-settings-panel__content">
        {/* Column Visibility Section */}
        <div className="data-list-settings-panel__section">
          <h4 className="data-list-settings-panel__section-title">
            {t('dataList.settings.columnVisibility', 'Column Visibility')}
          </h4>
          <div className="data-list-settings-panel__columns">
            {toggleableColumns.map((col) => (
              <div key={col.field} className="data-list-settings-panel__column-item">
                <Checkbox
                  inputId={`col-${col.field}`}
                  checked={settings.visibleColumns[col.field] !== false}
                  onChange={() => onToggleColumn(col.field)}
                />
                <label htmlFor={`col-${col.field}`}>{col.header}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Rows Per Page */}
        <div className="data-list-settings-panel__section">
          <div className="data-list-settings-panel__row">
            <label htmlFor="rowsPerPage">
              {t('dataList.settings.rowsPerPage', 'Rows per page')}
            </label>
            <DropdownAtom
              inputId="rowsPerPage"
              value={settings.rowsPerPage}
              options={rowsOptions}
              onChange={(e) => onUpdateSetting('rowsPerPage', e.value)}
              optionLabel="label"
              optionValue="value"
              className="data-list-settings-panel__dropdown"
              dropdownSize="small"
            />
          </div>
        </div>

        {/* Sort Options */}
        {sortOptions.length > 1 && (
          <div className="data-list-settings-panel__section">
            <div className="data-list-settings-panel__row">
              <label htmlFor="sortBy">
                {t('dataList.settings.sortBy', 'Sort by')}
              </label>
              <DropdownAtom
                inputId="sortBy"
                value={settings.sortField || ''}
                options={sortOptions}
                onChange={(e) => onUpdateSetting('sortField', e.value || null)}
                optionLabel="label"
                optionValue="value"
                className="data-list-settings-panel__dropdown"
                dropdownSize="small"
              />
            </div>
            {settings.sortField && (
              <div className="data-list-settings-panel__row">
                <label htmlFor="sortOrder">
                  {t('dataList.settings.sortOrder', 'Sort order')}
                </label>
                <DropdownAtom
                  inputId="sortOrder"
                  value={settings.sortOrder}
                  options={sortOrderOptions}
                  onChange={(e) => onUpdateSetting('sortOrder', e.value)}
                  optionLabel="label"
                  optionValue="value"
                  className="data-list-settings-panel__dropdown"
                  dropdownSize="small"
                />
              </div>
            )}
          </div>
        )}

        {/* Custom Settings Slot */}
        {customSettings && (
          <div className="data-list-settings-panel__section data-list-settings-panel__custom">
            {customSettings}
          </div>
        )}
      </div>
    </CustomDialog>
  );
};

export default DataListSettingsPanel;
